import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AiMessage, AiProvider, AiConversation, PcpChange, CustomTheme, ThinkSetting } from '@pcp/shared';
import { applyCustomTokens } from './customTheme';
import { isConnectionRequired, type ApiError } from './api';
import { CONNECTION_PROVIDER_LABELS, type ConnectionProvider } from './connectionsBridge';
import { isActivityCenterEnabled } from './featureFlags';
import { trackActivity } from './trackActivity';
import { readerLocale } from './locale';

// ─── Sidebar Store ────────────────────────────────────────────────

interface SidebarState {
  collapsed: boolean;
  mobileOpen: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  setMobileOpen: (open: boolean) => void;
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: false,
      mobileOpen: false,
      toggle: () => set((state) => ({ collapsed: !state.collapsed })),
      setCollapsed: (collapsed) => set({ collapsed }),
      setMobileOpen: (mobileOpen) => set({ mobileOpen }),
    }),
    {
      name: 'pcp-sidebar',
      partialize: (state) => ({ collapsed: state.collapsed }),
    },
  ),
);

// ─── Related / mention group collapse ─────────────────────────────
// Device-local expand/collapse for the InfoPanel related-items & mentions
// groups. Keyed by group *type* (e.g. `relation:Child`, `kind:ticket`) — folding
// "Children" once keeps every "Children" group folded across items and reloads.
// Groups are expanded by default (a key is present only while collapsed).

interface RelatedGroupState {
  collapsed: Record<string, true>;
  toggle: (key: string) => void;
}

export const useRelatedGroupStore = create<RelatedGroupState>()(
  persist(
    (set) => ({
      collapsed: {},
      toggle: (key) => set((state) => {
        const next = { ...state.collapsed };
        if (next[key]) delete next[key]; else next[key] = true;
        return { collapsed: next };
      }),
    }),
    {
      name: 'pcp-related-groups',
      partialize: (state) => ({ collapsed: state.collapsed }),
    },
  ),
);

// ─── Theme Store ──────────────────────────────────────────────────
// Two axes: `palette` (accent colour scheme) and `mode` (light/dark/system).
// Applied to <html data-theme data-mode>; CSS in index.css does the rest.

export type ThemePalette = 'signal-cyan' | 'slate-command' | 'verdant-ops' | 'indigo-grid' | 'rose-prism' | 'custom';
export type ThemeMode = 'light' | 'dark' | 'system';
/** Orthogonal accessibility axis: 'high' forces the AAA high-contrast tokens
 *  (light or dark per `mode`) on top of any palette. Default 'normal'. */
export type ThemeContrast = 'normal' | 'high';

export const THEME_PALETTES: { id: ThemePalette; name: string; accent: string; blurb: string }[] = [
  { id: 'signal-cyan', name: 'Signal Cyan', accent: '#0a93b4', blurb: 'Bright & monitoring' },
  { id: 'slate-command', name: 'Slate Command', accent: '#2f6feb', blurb: 'Professional & calm' },
  { id: 'verdant-ops', name: 'Verdant Ops', accent: '#0f9d6b', blurb: 'Fresh & operational' },
  { id: 'indigo-grid', name: 'Indigo Grid', accent: '#5b5bef', blurb: 'Structured & technical' },
  { id: 'rose-prism', name: 'Rose Prism', accent: '#d8456f', blurb: 'Premium & focused' },
];

interface ThemeState {
  palette: ThemePalette;
  mode: ThemeMode;
  contrast: ThemeContrast;
  /** The user's library of custom themes (sparse light/dark overrides). */
  customThemes: CustomTheme[];
  /** Selected custom theme id — used only when palette === 'custom'. */
  activeCustomId: string | null;
  setPalette: (palette: ThemePalette) => void;
  setMode: (mode: ThemeMode) => void;
  setContrast: (contrast: ThemeContrast) => void;
  /** Select a built-in accent palette. */
  selectBuiltin: (palette: ThemePalette) => void;
  /** Select a custom theme by id (sets palette to 'custom'). */
  selectCustom: (id: string) => void;
  /** Create or update a custom theme in the library. */
  upsertCustomTheme: (theme: CustomTheme) => void;
  /** Remove a custom theme; if it was active, fall back to its base palette. */
  deleteCustomTheme: (id: string) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      palette: 'signal-cyan',
      mode: 'light',
      contrast: 'normal',
      customThemes: [],
      activeCustomId: null,
      setPalette: (palette) => set({ palette }),
      setMode: (mode) => set({ mode }),
      setContrast: (contrast) => set({ contrast }),
      selectBuiltin: (palette) => set({ palette }),
      selectCustom: (id) => {
        if (get().customThemes.some((t) => t.id === id)) set({ palette: 'custom', activeCustomId: id });
      },
      upsertCustomTheme: (theme) => set((s) => {
        const i = s.customThemes.findIndex((t) => t.id === theme.id);
        return {
          customThemes: i >= 0
            ? s.customThemes.map((t) => (t.id === theme.id ? theme : t))
            : [...s.customThemes, theme],
        };
      }),
      deleteCustomTheme: (id) => set((s) => {
        const removed = s.customThemes.find((t) => t.id === id);
        const customThemes = s.customThemes.filter((t) => t.id !== id);
        return s.activeCustomId === id
          ? { customThemes, activeCustomId: null, palette: (removed?.base as ThemePalette) ?? 'signal-cyan' }
          : { customThemes };
      }),
    }),
    {
      name: 'pcp-theme-v2',
    },
  ),
);

/** The currently-selected custom theme object, or null when on a built-in palette. */
export function activeCustomTheme(
  s: Pick<ThemeState, 'palette' | 'customThemes' | 'activeCustomId'>,
): CustomTheme | null {
  if (s.palette !== 'custom' || !s.activeCustomId) return null;
  return s.customThemes.find((t) => t.id === s.activeCustomId) ?? null;
}

/** Resolve 'system' to a concrete light/dark using the OS preference. */
export function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

/** Apply palette + mode + contrast to <html> (data-theme / data-mode /
 *  data-contrast). data-contrast is only set when 'high' so the attribute is
 *  absent in the default case (and the high-contrast CSS never matches). */
export function applyTheme(
  palette: ThemePalette,
  mode: ThemeMode,
  contrast: ThemeContrast = 'normal',
  custom: CustomTheme | null = null,
) {
  const root = document.documentElement;
  root.setAttribute('data-theme', palette);
  root.setAttribute('data-mode', resolveMode(mode));
  if (contrast === 'high') root.setAttribute('data-contrast', 'high');
  else root.removeAttribute('data-contrast');
  // Custom themes inject inline vars; built-ins clear them (full revert).
  applyCustomTokens(palette === 'custom' ? custom : null, resolveMode(mode), contrast, root);
}

// ─── Chat Store ────────────────────────────────────────────────────

interface ChatState {
  sessions: AiConversation[];
  activeSessionId: string | null;

  messages: AiMessage[];
  isStreaming: boolean;
  currentThinking: string;
  currentContent: string;
  provider: string;
  model: string;
  think: ThinkSetting;
  numCtx: number | null;

  setSessions: (sessions: AiConversation[]) => void;
  setActiveSessionId: (id: string | null) => void;
  addSession: (session: AiConversation) => void;
  updateSession: (id: string, updates: Partial<AiConversation>) => void;
  removeSession: (id: string) => void;

  addMessage: (message: AiMessage) => void;
  setMessages: (messages: AiMessage[]) => void;
  clearMessages: () => void;
  setIsStreaming: (streaming: boolean) => void;
  setCurrentThinking: (thinking: string) => void;
  appendCurrentThinking: (chunk: string) => void;
  setCurrentContent: (content: string) => void;
  appendCurrentContent: (chunk: string) => void;
  resetStreaming: () => void;
  setProvider: (provider: string) => void;
  setModel: (model: string) => void;
  setThink: (think: ThinkSetting) => void;
  setNumCtx: (numCtx: number | null) => void;
}

export const useChatStore = create<ChatState>()((set) => ({
  sessions: [],
  activeSessionId: null,

  messages: [],
  isStreaming: false,
  currentThinking: '',
  currentContent: '',
  provider: 'openai',
  model: 'gpt-4o',
  think: 'medium',
  numCtx: null,

  setSessions: (sessions) => set((state) => {
    const active = sessions.find(s => s.id === state.activeSessionId) || sessions[0];
    return {
      sessions,
      activeSessionId: active ? active.id : null,
      messages: active ? active.messages : [],
      provider: active?.provider || 'openai',
      model: active?.model || 'gpt-4o',
      think: active?.think ?? (active?.provider === 'ollama' ? true : 'medium'),
      numCtx: active?.numCtx ?? null,
    };
  }),

  setActiveSessionId: (id) => set((state) => {
    const active = state.sessions.find(s => s.id === id);
    if (!active) return { activeSessionId: null, messages: [] };
    return {
      activeSessionId: id,
      messages: active.messages,
      provider: active.provider || 'openai',
      model: active.model || 'gpt-4o',
      think: active.think ?? (active.provider === 'ollama' ? true : 'medium'),
      numCtx: active.numCtx ?? null,
    };
  }),

  addSession: (session) => set((state) => ({
    sessions: [session, ...state.sessions],
  })),

  updateSession: (id, updates) => set((state) => {
    const newSessions = state.sessions.map(s => s.id === id ? { ...s, ...updates } : s);
    if (state.activeSessionId === id) {
      const updated = newSessions.find(s => s.id === id)!;
      return {
        sessions: newSessions,
        messages: updated.messages,
        provider: updated.provider || state.provider,
        model: updated.model || state.model,
        think: updated.think !== undefined ? updated.think : state.think,
        numCtx: updated.numCtx !== undefined ? updated.numCtx : state.numCtx,
      };
    }
    return { sessions: newSessions };
  }),

  removeSession: (id) => set((state) => {
    const newSessions = state.sessions.filter(s => s.id !== id);
    if (state.activeSessionId === id) {
      const active = newSessions[0];
      return {
        sessions: newSessions,
        activeSessionId: active ? active.id : null,
        messages: active ? active.messages : [],
        provider: active?.provider || 'openai',
        model: active?.model || 'gpt-4o',
        think: active?.think ?? (active?.provider === 'ollama' ? true : 'medium'),
        numCtx: active?.numCtx ?? null,
      };
    }
    return { sessions: newSessions };
  }),

  addMessage: (message) => set((state) => {
    const newMessages = [...state.messages, message];
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => 
        s.id === state.activeSessionId ? { ...s, messages: newMessages } : s
      );
      return { messages: newMessages, sessions: newSessions };
    }
    return { messages: newMessages };
  }),

  setMessages: (messages) => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => 
        s.id === state.activeSessionId ? { ...s, messages } : s
      );
      return { messages, sessions: newSessions };
    }
    return { messages };
  }),

  clearMessages: () => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => 
        s.id === state.activeSessionId ? { ...s, messages: [] } : s
      );
      return { messages: [], sessions: newSessions, currentThinking: '', currentContent: '' };
    }
    return { messages: [], currentThinking: '', currentContent: '' };
  }),

  setIsStreaming: (isStreaming) => set({ isStreaming }),
  setCurrentThinking: (currentThinking) => set({ currentThinking }),
  appendCurrentThinking: (chunk) =>
    set((state) => ({ currentThinking: state.currentThinking + chunk })),
  setCurrentContent: (currentContent) => set({ currentContent }),
  appendCurrentContent: (chunk) =>
    set((state) => ({ currentContent: state.currentContent + chunk })),
  resetStreaming: () =>
    set({ isStreaming: false, currentThinking: '', currentContent: '' }),
  setProvider: (provider) => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => s.id === state.activeSessionId ? { ...s, provider } : s);
      return { provider, sessions: newSessions };
    }
    return { provider };
  }),
  setModel: (model) => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => s.id === state.activeSessionId ? { ...s, model } : s);
      return { model, sessions: newSessions };
    }
    return { model };
  }),
  setThink: (think) => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => s.id === state.activeSessionId ? { ...s, think } : s);
      return { think, sessions: newSessions };
    }
    return { think };
  }),
  setNumCtx: (numCtx) => set((state) => {
    if (state.activeSessionId) {
      const newSessions = state.sessions.map(s => s.id === state.activeSessionId ? { ...s, numCtx } : s);
      return { numCtx, sessions: newSessions };
    }
    return { numCtx };
  }),
}));

// ─── Toast Store ──────────────────────────────────────────────────

export interface Toast {
  id: string;
  // 'progress' = a live, sticky toast (spinner, no auto-dismiss) for background
  // work invoked from the UI; updated via updateToast as progress streams in.
  type: 'success' | 'error' | 'info' | 'progress';
  message: string;
  /** Optional in-app action rendered as a button: a react-router `href`, or an
   *  `onClick` (e.g. the filter bar's "Undo"). Either way it dismisses the toast. */
  action?: { label: string; href?: string; onClick?: () => void };
  /** Auto-dismiss delay in ms. Default 4s (8s for errors so they're readable);
   *  0 keeps it until dismissed. Ignored for 'progress' (always sticky). */
  durationMs?: number;
}

interface ToastState {
  toasts: Toast[];
  /** Add a toast; returns its id so callers can updateToast() it live. Non-progress
   *  toasts auto-dismiss after 4s; 'progress' toasts stay until updated/removed. */
  addToast: (toast: Omit<Toast, 'id'>) => string;
  /** Patch an existing toast (e.g. live progress message, or resolve a progress
   *  toast to success/error — which then auto-dismisses). */
  updateToast: (id: string, patch: Partial<Omit<Toast, 'id'>>) => void;
  removeToast: (id: string) => void;
}

// Default auto-dismiss: errors linger longer so they're actually readable; a
// caller can override via `durationMs` (0 = sticky until dismissed).
const dismissMs = (toast: Pick<Toast, 'type' | 'durationMs'>) =>
  toast.durationMs ?? (toast.type === 'error' ? 8000 : 4000);
const autoDismiss = (set: any, id: string, ms: number) => {
  if (ms <= 0) return; // sticky
  setTimeout(() => set((state: ToastState) => ({ toasts: state.toasts.filter((t) => t.id !== id) })), ms);
};

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    if (toast.type !== 'progress') autoDismiss(set, id, dismissMs(toast));
    return id;
  },
  updateToast: (id, patch) => {
    set((state) => ({ toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
    // Resolving a progress toast to a terminal type → auto-dismiss like a normal one.
    if (patch.type && patch.type !== 'progress') autoDismiss(set, id, dismissMs(patch as Toast));
  },
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));

/**
 * When the error is a 412 "connect your account first" rejection, return true
 * so the caller suppresses its own error toast — the GLOBAL
 * ConnectionRequiredSheet (popped by the api layer via connectionsBridge) is
 * the single prompt surface. Returns false for any other error, so the
 * caller's normal error handling still runs. Use in mutation catch paths for
 * user-attributed writes.
 */
export function toastConnectionRequired(e: unknown): boolean {
  return isConnectionRequired(e);
}

/**
 * Run a UI-invoked background task with a single live progress toast: it appears
 * immediately, updates as `onProgress` fires (e.g. from an SSE stream), then
 * resolves to a success/error toast. Use this for ANY background work kicked off
 * from the UI so the user always sees what's happening.
 *
 * Under the Unified Activity Center dark-launch this is a thin shim over
 * `trackActivity` (zero callsite changes): the work also reports into the central
 * Activity Center so it shows up alongside syncs. The terminal success/error
 * toast still fires either way. When the flag is OFF it is the legacy toast-only
 * path, byte-for-byte.
 */
export async function runWithProgressToast<T>(
  label: string,
  fn: (onProgress: (message: string) => void) => Promise<T>,
  done?: (result: T) => string,
): Promise<T> {
  const { addToast, updateToast } = useToastStore.getState();
  const id = addToast({ type: 'progress', message: `${label}…` });

  const finishOk = (result: T) =>
    updateToast(id, { type: 'success', message: done ? done(result) : `${label} — done.` });
  const finishErr = (e: any) =>
    updateToast(id, { type: 'error', message: `${label} failed: ${e?.message ?? e}` });

  // Flag ON → also mirror the task into the Activity Center via trackActivity.
  if (isActivityCenterEnabled()) {
    try {
      const result = await trackActivity(
        { source: 'backfill', kind: 'task', title: label },
        (reporter) => fn((message) => {
          updateToast(id, { message: message || `${label}…` });
          reporter.onProgress(message);
        }),
      );
      finishOk(result);
      return result;
    } catch (e: any) {
      finishErr(e);
      throw e;
    }
  }

  // Flag OFF → legacy toast-only behaviour.
  try {
    const result = await fn((message) => updateToast(id, { message: message || `${label}…` }));
    finishOk(result);
    return result;
  } catch (e: any) {
    finishErr(e);
    throw e;
  }
}

// ─── Analysis Task Store ──────────────────────────────────────────

export interface AnalysisLogEntry {
  type: 'info' | 'success' | 'error';
  text: string;
  timestamp: string;
}

export interface AnalysisTask {
  id: string;
  type: 'discourse-analysis' | 'release-preparation' | 'commit-analysis' | 'zoho-analysis' | 'azure-next-steps';
  status: 'pending' | 'running' | 'done' | 'failed';
  /** Optional for release-preparation, which now resolves the model from Settings. */
  provider?: string;
  model?: string;
  think?: ThinkSetting;
  numCtx?: number | null;

  // Discourse specific
  topics?: Array<{ id: number; title: string }>;
  /** When true, also generate "next steps" for each topic after analysis (AI assist). */
  withNextSteps?: boolean;
  /** Item ids currently being processed (supports parallel workers). Number for
   *  topics/work items, string for Zoho ticket ids. */
  activeIds?: Array<number | string>;

  // Zoho ticket specific
  tickets?: Array<{ id: string; subject: string }>;

  // Azure DevOps work-item specific (next-steps only — no analysis feature)
  workItems?: Array<{ id: number; title: string }>;

  // Release specific
  selectedApps?: string[];
  /** Release name (user-entered or AI-drafted). */
  name?: string;
  /** Scope mode for release preparation. */
  scopeMode?: 'out-of-sync' | 'custom-period';
  /** Custom-period window (ISO), null for out-of-sync. */
  periodStart?: string | null;
  periodEnd?: string | null;

  // Commit specific
  commitIds?: string[];
  
  // State
  currentIndex: number;
  totalItems: number;
  succeeded: number;
  failed: number;
  logs: AnalysisLogEntry[];
  streamingContent?: string;
  analysisId?: string;
  sessionId?: string;
  startedAt: string;
}

interface AnalysisState {
  tasks: AnalysisTask[];
  panelOpen: boolean;
  addTask: (task: Omit<AnalysisTask, 'id' | 'currentIndex' | 'succeeded' | 'failed' | 'logs' | 'startedAt' | 'status'>) => string;
  updateTask: (id: string, updates: Partial<AnalysisTask>) => void;
  addLog: (taskId: string, entry: Omit<AnalysisLogEntry, 'timestamp'>) => void;
  appendStreamingContent: (taskId: string, content: string) => void;
  clearStreamingContent: (taskId: string) => void;
  removeTask: (id: string) => void;
  clearDone: () => void;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
}

export const useAnalysisStore = create<AnalysisState>()((set) => ({
  tasks: [],
  panelOpen: false,
  addTask: (task) => {
    const id = crypto.randomUUID();
    set((state) => ({
      tasks: [...state.tasks, {
        ...task,
        id,
        type: task.type || 'discourse-analysis',
        status: 'running',
        currentIndex: 0,
        succeeded: 0,
        failed: 0,
        logs: [],
        startedAt: new Date().toISOString(),
      }],
      // Always appear collapsed (minimized bar); the user expands if they want.
      panelOpen: false,
    }));
    return id;
  },
  updateTask: (id, updates) => set((state) => ({
    tasks: state.tasks.map((t) => t.id === id ? { ...t, ...updates } : t),
  })),
  addLog: (taskId, entry) => set((state) => ({
    tasks: state.tasks.map((t) => t.id === taskId ? {
      ...t,
      logs: [...t.logs, { ...entry, timestamp: new Date().toLocaleTimeString(readerLocale()) }],
    } : t),
  })),
  appendStreamingContent: (taskId, content) => set((state) => ({
    tasks: state.tasks.map((t) => t.id === taskId ? {
      ...t,
      streamingContent: (t.streamingContent || '') + content,
    } : t),
  })),
  clearStreamingContent: (taskId) => set((state) => ({
    tasks: state.tasks.map((t) => t.id === taskId ? {
      ...t,
      streamingContent: '',
    } : t),
  })),
  removeTask: (id) => set((state) => ({
    tasks: state.tasks.filter((t) => t.id !== id),
  })),
  clearDone: () => set((state) => ({
    tasks: state.tasks.filter((t) => t.status === 'running'),
  })),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
  togglePanel: () => set((state) => ({ panelOpen: !state.panelOpen })),
}));

// ─── Data Freshness Store ─────────────────────────────────────────
// Signals from the realtime bridge (RealtimeBridge) to detail/edit surfaces: when
// an entity changes anywhere, a detail page compares what it last loaded against
// the latest change to decide whether to show a refresh / edit-conflict banner —
// without yanking the content the user is reading. Two granularities:
//   • coarse per-DOMAIN `versions` — bumped ONLY by id-less changes (a large sync
//     batch, or a reconnect probe that can't name ids); the whole-domain fallback.
//   • precise per-ENTITY `entities['${domain}:${id}']` — the exact item's latest
//     change cursor + who made it, so a page for item X reacts to X and IGNORES a
//     sibling's change. (Lists are refetched directly by the bridge, not via these.)
// `azureReorderActive` lets the bridge defer Backlog-list refetches during a drag.

export type DataDomain = 'discourse' | 'azure' | 'zoho' | 'commits' | 'requests' | 'knowledge' | 'feedback';

export interface EntityFreshness {
  /** ev.cursor of the latest change to this entity (global monotonic). */
  cursor: number;
  /** Who made it: a user id, or null for system/sync (and unattributed writes). */
  actorUserId: string | null;
  /** The change kind — lets a banner distinguish "deleted" from "updated". */
  change: PcpChange;
  /** ev.ts (epoch ms) — used for LRU eviction of the bounded map. */
  ts: number;
}

// Detail pages read ~1 entry each; cap the map so a long-lived session that sees
// thousands of changes can't grow it without bound.
const MAX_FRESHNESS_ENTITIES = 200;

interface DataFreshnessState {
  versions: Record<DataDomain, number>;
  /** Actor of the most recent change per domain (drives coarse self-suppression). */
  lastActorByDomain: Partial<Record<DataDomain, string | null>>;
  /** Precise per-entity freshness, key = `${domain}:${id}` (id stringified). LRU-capped. */
  entities: Record<string, EntityFreshness>;
  /** Record an id-less / reconnect change → whole-domain stale (the coarse fallback). */
  bump: (domain: DataDomain, actorUserId?: string | null) => void;
  /** Record a change carrying a specific entity id. Does NOT bump the coarse version,
   *  so a detail page for a DIFFERENT id stays quiet. */
  markEntity: (
    domain: DataDomain,
    id: string | number,
    actorUserId: string | null,
    change: PcpChange,
    cursor: number,
    ts: number,
  ) => void;
  azureReorderActive: boolean;
  setAzureReorderActive: (active: boolean) => void;
}

export const useDataFreshnessStore = create<DataFreshnessState>()((set) => ({
  versions: { discourse: 0, azure: 0, zoho: 0, commits: 0, requests: 0, knowledge: 0, feedback: 0 },
  lastActorByDomain: {},
  entities: {},
  bump: (domain, actorUserId = null) =>
    set((state) => ({
      versions: { ...state.versions, [domain]: state.versions[domain] + 1 },
      lastActorByDomain: { ...state.lastActorByDomain, [domain]: actorUserId },
    })),
  markEntity: (domain, id, actorUserId, change, cursor, ts) =>
    set((state) => {
      const key = `${domain}:${String(id)}`;
      const prev = state.entities[key];
      // Monotonic guard: ignore a strictly-older (out-of-order / replayed) event.
      if (prev && cursor < prev.cursor) return state;
      let entities: Record<string, EntityFreshness> = { ...state.entities, [key]: { cursor, actorUserId, change, ts } };
      // LRU-prune by oldest ts once over the cap (only runs above 200 entries).
      const keys = Object.keys(entities);
      if (keys.length > MAX_FRESHNESS_ENTITIES) {
        let oldestKey = keys[0];
        for (const k of keys) if (entities[k].ts < entities[oldestKey].ts) oldestKey = k;
        const { [oldestKey]: _drop, ...rest } = entities;
        entities = rest;
      }
      return { entities };
    }),
  azureReorderActive: false,
  setAzureReorderActive: (azureReorderActive) => set({ azureReorderActive }),
}));

// ─── Live-connection status store ─────────────────────────────────
// Written ONLY by RealtimeBridge (the single EventSource owner); read by the
// sidebar ConnectionStatus indicator. Surfaces the live SSE connection's health
// so a dropped/zombie stream is visible instead of silently stale.

export type ConnStatus = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'paused';

export interface ConnLogEntry {
  /** epoch ms of the transition */
  ts: number;
  status: ConnStatus;
  /** short human note, e.g. "Connection lost", "Reconnected" */
  note: string;
}

const MAX_CONN_LOG = 10;

const CONN_DEFAULT_NOTE: Record<ConnStatus, string> = {
  connecting: 'Connecting',
  live: 'System online',
  reconnecting: 'Connection lost',
  offline: 'Offline',
  paused: 'Paused (tab hidden)',
};

interface ConnectionStoreState {
  status: ConnStatus;
  /** Last REAL change event received (not a ping) — drives "last event Xs ago". */
  lastEventAt: number | null;
  /** Successful reconnects this session (recovery after a drop). */
  reconnects: number;
  /** Recent transitions, newest last, capped at MAX_CONN_LOG. */
  log: ConnLogEntry[];
  /** Set the current status. Appends a log entry ONLY when the status changes
   *  (consecutive dupes are ignored); increments `reconnects` on a genuine recovery. */
  setStatus: (status: ConnStatus, note?: string) => void;
  /** Record arrival of a real change event (updates lastEventAt). */
  noteEvent: () => void;
}

export const useConnectionStore = create<ConnectionStoreState>()((set) => ({
  status: 'connecting',
  lastEventAt: null,
  reconnects: 0,
  log: [],
  setStatus: (status, note) =>
    set((state) => {
      if (state.status === status) return state; // dedup consecutive identical states
      const recovered = status === 'live' && (state.status === 'reconnecting' || state.status === 'offline');
      const text = note ?? (recovered ? 'Reconnected' : CONN_DEFAULT_NOTE[status]);
      const entry: ConnLogEntry = { ts: Date.now(), status, note: text };
      return {
        status,
        log: [...state.log, entry].slice(-MAX_CONN_LOG),
        reconnects: recovered ? state.reconnects + 1 : state.reconnects,
      };
    }),
  noteEvent: () => set({ lastEventAt: Date.now() }),
}));

// ─── Support Chat Store ───────────────────────────────────────────
// Drives the embedded RealEquity B2B customer support widget. Self-contained
// (its own conversation, isolated from the internal assistant `useChatStore`)
// so the same widget+store can be lifted into the B2B portal. Async actions call
// the support endpoints directly. Persisted so a chat survives reload/navigation.

import type { SupportChatMessage, SupportChatStatus, SupportStreamEvent } from '@pcp/shared';
import { startSupportChat, sendSupportMessage, syncSupportMessages, supportStreamUrl, sendSupportTyping, endSupportChat } from './api';

interface SupportChatState {
  /** The panel is expanded (vs. collapsed to the launcher bubble). */
  open: boolean;
  /** A conversation has been successfully started this session. */
  started: boolean;
  conversationId: string | null;
  agentName: string;
  agentTitle: string;
  /** Thread lifecycle — flips to 'escalated' once the request is sent to support. */
  status: SupportChatStatus;
  messages: SupportChatMessage[];
  /** Waiting on the agent's reply (drives the typing indicator + input lock). */
  awaiting: boolean;
  /** Unread agent messages while the panel is collapsed (launcher badge). */
  unread: number;
  /** A human teammate has taken over — the AI no longer auto-replies. */
  humanHandling: boolean;
  /** A teammate is currently typing a reply (transient; auto-clears). */
  agentTyping: boolean;

  /** Open the widget, starting a fresh conversation if none exists. */
  openWidget: () => Promise<void>;
  /** Start a brand-new conversation (used by the "Support Chat" new-conversation choice). */
  startFresh: () => Promise<void>;
  /** Collapse to the launcher bubble (keeps history). */
  minimize: () => void;
  /** End the conversation entirely. */
  end: () => void;
  /** Send a customer message; `quickReplyLabel` is set when it came from tapping a chip. */
  send: (text: string, quickReplyLabel?: string) => Promise<void>;
  /** Poll for new agent/human messages (live human replies) while the chat is open. */
  sync: () => Promise<void>;
  /** Open an SSE stream for instant delivery of agent/human replies + status. */
  connectStream: () => void;
  /** Close the SSE stream. */
  disconnectStream: () => void;
  /** Tell the operator the customer is typing (throttled fire-and-forget). */
  notifyTyping: () => void;
  /** Customer ends the conversation (no more help needed) → closes it server-side. */
  endConversation: () => Promise<void>;
}

function nowIso() { return new Date().toISOString(); }

// The realtime SSE connection lives outside the store (EventSource is not
// serializable and must never be persisted). We track which conversation it's
// bound to so connectStream() is idempotent across re-renders.
let supportEventSource: EventSource | null = null;
let supportStreamConvId: string | null = null;
// Auto-clear timer for the "teammate is typing" indicator, and a throttle stamp
// for the customer's own outgoing typing pings.
let agentTypingTimer: ReturnType<typeof setTimeout> | null = null;
let lastTypingPingAt = 0;
// Guards a one-shot resend after the chat was auto-deleted (404) → start fresh.
let sendRecovering = false;

export const useSupportChatStore = create<SupportChatState>()(
  persist(
    (set, get) => ({
      open: false,
      started: false,
      conversationId: null,
      agentName: 'Support',
      agentTitle: 'RealEquity Support',
      status: 'active',
      messages: [],
      awaiting: false,
      unread: 0,
      humanHandling: false,
      agentTyping: false,

      openWidget: async () => {
        set({ open: true, unread: 0 });
        if (!get().started && !get().awaiting) await get().startFresh();
      },

      startFresh: async () => {
        if (get().awaiting) return;
        // Do NOT mark `started` until the call succeeds — a failed first attempt
        // must leave the widget retryable (openWidget re-runs startFresh).
        set({ open: true, awaiting: true, started: false, status: 'active', messages: [], unread: 0, humanHandling: false });
        try {
          const r = await startSupportChat({});
          set({
            started: true,
            conversationId: r.conversationId,
            agentName: r.agentName,
            agentTitle: r.agentTitle,
            status: r.status,
            messages: [r.message],
            awaiting: false,
            humanHandling: false,
          });
        } catch (e: any) {
          set({
            started: false,
            conversationId: null,
            awaiting: false,
            messages: [{
              id: crypto.randomUUID(), role: 'agent', createdAt: nowIso(),
              content: "Sorry — I couldn't connect just now. Tap to try again.",
              quickReplies: [{ label: 'Try again', value: '__retry__' }],
            }],
          });
        }
      },

      minimize: () => set({ open: false, unread: 0 }),

      end: () => set({
        open: false, started: false, conversationId: null,
        status: 'active', messages: [], awaiting: false, unread: 0, humanHandling: false,
      }),

      send: async (text, quickReplyLabel) => {
        const body = (text ?? '').trim();
        const { conversationId, awaiting, started } = get();
        if (!body || awaiting) return;
        // Retry affordance from a failed start.
        if (body === '__retry__' || !conversationId || !started) {
          await get().startFresh();
          return;
        }
        const userMsg: SupportChatMessage = { id: crypto.randomUUID(), role: 'user', content: body, createdAt: nowIso() };
        // Drop any stale quick-replies on the prior agent turn once the user moves on.
        set((s) => ({
          messages: [...s.messages.map((m) => (m.role === 'agent' ? { ...m, quickReplies: [] } : m)), userMsg],
          awaiting: true,
        }));
        try {
          const r = await sendSupportMessage(conversationId, body, quickReplyLabel);
          // A null message = a human is handling the thread; no AI reply this turn.
          // The widget's polling/SSE picks up the human's reply.
          set((s) => {
            // The realtime stream may have already delivered this reply — dedup by id.
            const dup = r.message ? s.messages.some((m) => m.id === r.message!.id) : true;
            return {
              messages: r.message && !dup ? [...s.messages, r.message] : s.messages,
              status: r.status,
              humanHandling: r.humanHandling ?? s.humanHandling,
              awaiting: false,
              // Only clear unread when the panel is open (user is looking). If the
              // panel is closed and SSE already delivered+counted this reply (dup),
              // leave its badge intact — don't reset it to 0. Increment only when
              // this HTTP response is the first to deliver a genuinely new reply.
              unread: s.open ? 0 : (r.message && !dup ? s.unread + 1 : s.unread),
            };
          });
        } catch (e: any) {
          // The chat may have been auto-deleted after going idle (404) — recover
          // transparently by starting a fresh conversation and resending once.
          if (e?.statusCode === 404 && !sendRecovering) {
            sendRecovering = true;
            try {
              await get().startFresh();
              if (get().conversationId) await get().send(body, quickReplyLabel);
            } finally { sendRecovering = false; }
            return;
          }
          set((s) => ({
            awaiting: false,
            messages: [...s.messages, {
              id: crypto.randomUUID(), role: 'agent', createdAt: nowIso(),
              content: "Sorry — something went wrong sending that. Could you try again?",
            }],
          }));
        }
      },

      sync: async () => {
        const { conversationId, awaiting } = get();
        if (!conversationId || awaiting) return;
        // Cursor: the newest agent/human message we already have.
        const lastAgent = [...get().messages].reverse().find((m) => m.role === 'agent');
        try {
          const r = await syncSupportMessages(conversationId, lastAgent?.createdAt);
          set((s) => {
            const have = new Set(s.messages.map((m) => m.id));
            const incoming = r.messages.filter((m) => !have.has(m.id));
            if (!incoming.length && r.status === s.status && r.humanHandling === s.humanHandling) return s;
            return {
              messages: incoming.length ? [...s.messages, ...incoming] : s.messages,
              status: r.status,
              humanHandling: r.humanHandling,
              unread: s.open ? 0 : s.unread + incoming.length,
            };
          });
        } catch { /* ignore transient poll errors */ }
      },

      connectStream: () => {
        if (typeof EventSource === 'undefined') return; // SSR / unsupported → poll-only
        const { conversationId, started } = get();
        if (!conversationId || !started) return;
        // Already streaming this conversation? Nothing to do.
        if (supportEventSource && supportStreamConvId === conversationId) return;
        get().disconnectStream();

        const es = new EventSource(supportStreamUrl(conversationId));
        supportEventSource = es;
        supportStreamConvId = conversationId;

        es.onmessage = (e: MessageEvent) => {
          let ev: SupportStreamEvent;
          try { ev = JSON.parse(e.data as string); } catch { return; }
          if (ev.type === 'message') {
            // Only agent/human replies are relevant to the customer — their own
            // messages are already shown optimistically (the user echo on the bus
            // is for the operator view).
            if (ev.message.role !== 'agent') return;
            set((s) => {
              if (s.messages.some((m) => m.id === ev.message.id)) return s;
              return {
                messages: [...s.messages, ev.message],
                unread: s.open ? 0 : s.unread + 1,
              };
            });
          } else if (ev.type === 'status') {
            set((s) => ({
              status: ev.status,
              humanHandling: ev.humanHandling ?? s.humanHandling,
            }));
          } else if (ev.type === 'typing' && ev.who === 'agent') {
            // A teammate is typing → show the indicator and auto-clear it if no
            // further pings arrive (they're sent ~every 2s while typing).
            set({ agentTyping: true });
            if (agentTypingTimer) clearTimeout(agentTypingTimer);
            agentTypingTimer = setTimeout(() => set({ agentTyping: false }), 4000);
          }
        };
        // On error EventSource auto-reconnects; the fallback poll covers any gap.
        es.onerror = () => { /* keep open; browser retries automatically */ };
      },

      disconnectStream: () => {
        if (supportEventSource) {
          supportEventSource.close();
          supportEventSource = null;
          supportStreamConvId = null;
        }
        if (agentTypingTimer) { clearTimeout(agentTypingTimer); agentTypingTimer = null; }
        set({ agentTyping: false });
      },

      notifyTyping: () => {
        const { conversationId, started } = get();
        if (!conversationId || !started) return;
        const now = Date.now();
        if (now - lastTypingPingAt < 1800) return; // throttle keystrokes → ~one ping/2s
        lastTypingPingAt = now;
        sendSupportTyping(conversationId, 'customer');
      },

      endConversation: async () => {
        const { conversationId } = get();
        if (!conversationId) return;
        // Optimistically reflect the close; the SSE status event confirms it too.
        set({ status: 'closed', awaiting: false, agentTyping: false });
        try { await endSupportChat(conversationId); } catch { /* already closed / gone */ }
      },
    }),
    {
      name: 'pcp-support-chat',
      // Persist the thread + identity, but never the transient open/awaiting flags
      // (so a reload restores the conversation to the collapsed launcher).
      partialize: (s) => ({
        started: s.started,
        conversationId: s.conversationId,
        agentName: s.agentName,
        agentTitle: s.agentTitle,
        status: s.status,
        messages: s.messages,
        humanHandling: s.humanHandling,
      }),
    },
  ),
);
